import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { checkHRSubscription, SubscriptionStatus } from '../utils/subscriptionUtils';

interface Subscription {
  plan: 'free' | 'pro' | 'none' | 'core' | 'nexora' | 'enterprise';
  status: 'active' | 'inactive' | 'expired';
  startDate: Date | null;
  expiresAt: Date | null;
}

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  isPro: boolean;
  isCore: boolean;
  isNexora: boolean;
  isEnterprise: boolean;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  hasActiveSubscription: boolean;
  needsSubscription: boolean;
  subscriptionStatus?: SubscriptionStatus;
}

export const useSubscription = (): UseSubscriptionReturn => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [hrSubscriptionStatus, setHRSubscriptionStatus] = useState<SubscriptionStatus | undefined>();

  const fetchSubscription = async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const db = getFirestore(firebaseApp);
      const collectionName = user.userType === 'hr' ? 'recruiters' : 'jobseekers';
      const userDoc = await getDoc(doc(db, collectionName, user.id));

      if (userDoc.exists()) {
        const data = userDoc.data();

        // For HR users, check new subscription model
        if (user.userType === 'hr') {
          const hrStatus = await checkHRSubscription(user.id);
          setHRSubscriptionStatus(hrStatus);

          // Check NEW subscription object format first, then fall back to old format
          const subData = data.subscription;

          if (subData && subData.status === 'active' && subData.plan !== 'none') {
            // New subscription format
            const expiresAt = subData.expiresAt?.toDate
              ? subData.expiresAt.toDate()
              : (subData.expiresAt ? new Date(subData.expiresAt) : null);
            const startDate = subData.startDate?.toDate
              ? subData.startDate.toDate()
              : (subData.startDate ? new Date(subData.startDate) : null);

            setSubscription({
              plan: subData.plan as 'core' | 'nexora' | 'enterprise',
              status: 'active',
              startDate: startDate,
              expiresAt: expiresAt,
            });
          } else if (hrStatus.hasSubscription && hrStatus.isActive) {
            // Fallback to old format
            const planName = data.subscriptionPlan?.toLowerCase() || 'none';
            setSubscription({
              plan: planName as 'core' | 'nexora' | 'enterprise',
              status: 'active',
              startDate: data.startDate ? new Date(data.startDate) : null,
              expiresAt: data.endDate ? new Date(data.endDate) : null,
            });
          } else {
            setSubscription({
              plan: 'none',
              status: hrStatus.isExpired ? 'expired' : 'inactive',
              startDate: null,
              expiresAt: null,
            });
          }
        } else {
          // For job seekers, use old subscription model
          const subscriptionData = data.subscription;


          if (subscriptionData) {
            const parsedSubscription = {
              plan: subscriptionData.plan,
              status: subscriptionData.status,
              startDate: subscriptionData.startDate?.toDate ? subscriptionData.startDate.toDate() : null,
              expiresAt: subscriptionData.expiresAt?.toDate ? subscriptionData.expiresAt.toDate() : null,
            };


            setSubscription(parsedSubscription);
          } else {
            // FALLBACK: Default subscription for old users without subscription field
            const defaultSubscription = {
              plan: 'free' as const,
              status: 'active' as const,
              startDate: null,
              expiresAt: null,
            };

            setSubscription(defaultSubscription);
          }
        }
      }
    } catch (error) {
      // Default to free plan on error
      setSubscription({
        plan: user?.userType === 'jobseeker' ? 'free' : 'none',
        status: 'active',
        startDate: null,
        expiresAt: null,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();

    // Re-check subscription every 5 minutes for HR users
    if (user?.userType === 'hr') {
      const interval = setInterval(fetchSubscription, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const isPro = subscription?.plan === 'pro' && subscription?.status === 'active';
  const isCore = subscription?.plan === 'core' && subscription?.status === 'active';
  const isNexora = subscription?.plan === 'nexora' && subscription?.status === 'active';
  const isEnterprise = subscription?.plan === 'enterprise' && subscription?.status === 'active';

  // For HR users, need both active status AND a valid plan (not 'none')
  const hasActiveSubscription = subscription?.status === 'active' && subscription?.plan !== 'none';
  const needsSubscription = user?.userType === 'hr' && (!subscription || subscription.plan === 'none' || subscription.status !== 'active');

  return {
    subscription,
    isPro,
    isCore,
    isNexora,
    isEnterprise,
    loading,
    refreshSubscription: fetchSubscription,
    hasActiveSubscription,
    needsSubscription,
    subscriptionStatus: hrSubscriptionStatus,
  };
};
